import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, ShoppingCart, Truck, Package, TrendingUp } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { BrandLogo } from "@/components/BrandLogo";

const PERSONAS = [
  { role: "buyer",      label: "Importer / Buyer",   sub: "I import & sell premium food",      icon: ShoppingCart, accent: "text-blue-400"   },
  { role: "exporter",   label: "Exporter",           sub: "I produce & ship to global buyers", icon: Truck,        accent: "text-purple-400" },
  { role: "downstream", label: "Downstream Buyer",   sub: "I source for retail / restaurants", icon: Package,      accent: "text-cyan-400"   },
  { role: "investor",   label: "Investor",           sub: "I deploy capital across trade flows", icon: TrendingUp, accent: "text-[#22C55E]"  },
];

function ApplyDropdown({ openModal, testid, label = "Apply for Access", onItem }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="bg-[#16A34A] text-white hover:bg-[#15803D] transition-all px-5 py-2 text-sm font-medium flex items-center gap-1.5"
          data-testid={testid}
        >
          {label} <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[#09120D] border-[#1A2E22] text-white w-[280px] p-1"
        data-testid={`${testid}-menu`}
      >
        <DropdownMenuLabel className="text-[#A1B2A8] text-[10px] uppercase tracking-[0.2em] font-semibold px-3 pt-2 pb-1">
          Choose your role
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#1A2E22]" />
        {PERSONAS.map((p) => (
          <DropdownMenuItem
            key={p.role}
            onSelect={() => { openModal(p.role); onItem?.(); }}
            className="cursor-pointer focus:bg-[#0D1B13] py-2.5 px-3 flex items-start gap-3"
            data-testid={`apply-dropdown-${p.role}`}
          >
            <p.icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.accent}`} />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium leading-tight">{p.label}</p>
              <p className="text-[#A1B2A8] text-[11px] leading-snug mt-0.5">{p.sub}</p>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Navbar({ openModal }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#050A07]/90 backdrop-blur-xl border-b border-[#1A2E22]"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="#hero" className="inline-flex items-center" data-testid="navbar-logo" aria-label="Delta GreenX home">
          <BrandLogo as="div" size="md" />
        </a>

        <div className="hidden md:flex items-center gap-8">
          <a href="#what-is" className="text-sm text-[#A1B2A8] hover:text-white transition-colors">What is GreenX</a>
          <a href="#ai-engine" className="text-sm text-[#A1B2A8] hover:text-white transition-colors">AI Engine</a>
          <a href="#market-reality" className="text-sm text-[#A1B2A8] hover:text-white transition-colors">Market</a>
          <a href="#how-it-works" className="text-sm text-[#A1B2A8] hover:text-white transition-colors">How It Works</a>
          <Link
            to="/sign-in"
            className="text-sm text-white hover:text-[#22C55E] transition-colors border border-[#1A2E22] hover:border-[#22C55E] px-4 py-1.5"
            data-testid="navbar-signin-btn"
          >
            Already Registered? Sign In
          </Link>
          <ApplyDropdown openModal={openModal} testid="navbar-apply-btn" />
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-[#050A07]/95 backdrop-blur-xl border-b border-[#1A2E22] px-6 pb-4 space-y-3">
          <a href="#what-is" onClick={() => setMobileOpen(false)} className="block text-sm text-[#A1B2A8] hover:text-white">What is GreenX</a>
          <a href="#ai-engine" onClick={() => setMobileOpen(false)} className="block text-sm text-[#A1B2A8] hover:text-white">AI Engine</a>
          <a href="#market-reality" onClick={() => setMobileOpen(false)} className="block text-sm text-[#A1B2A8] hover:text-white">Market</a>
          <a href="#how-it-works" onClick={() => setMobileOpen(false)} className="block text-sm text-[#A1B2A8] hover:text-white">How It Works</a>
          <Link
            to="/sign-in"
            onClick={() => setMobileOpen(false)}
            className="block text-sm text-white hover:text-[#22C55E] border border-[#1A2E22] hover:border-[#22C55E] px-3 py-2"
            data-testid="mobile-signin-btn"
          >
            Already Registered? Sign In
          </Link>
          <div className="pt-1">
            <ApplyDropdown openModal={openModal} testid="mobile-apply-btn" onItem={() => setMobileOpen(false)} />
          </div>
        </div>
      )}
    </nav>
  );
}
